import { CfgQuestionGroup } from '../app/types/catalog-types'
import { CfgViewQuestion, CfgViewQuestionSingle } from '../app/configurator-logic'
import { ConfiguratorEnumQuestion } from './configurator-q-enum'
import { ConfiguratorMaterialQuestion, MaterialQuestionInteraction } from './configurator-q-material'
import { OptionValues } from '../app/types/composition'
import { ConfiguratorHeading } from './configurator-q-heading'
import { ConfiguratorQuantityQuestion } from './configurator-q-quantity'

export const ConfiguratorQuestion = ({ question, step, questionData, values, implicitValues, maxShowablePerCounter,
                                       onValuesChange, onQuestionDataChange }: {
    question: CfgViewQuestion
    step: number
    questionData?: any
    values: OptionValues
    implicitValues: OptionValues
    maxShowablePerCounter: Record<string,number>
    onValuesChange: (values: OptionValues, forceApplytoAll: boolean) => void
    onQuestionDataChange: (data: any, interaction: MaterialQuestionInteraction) => void
}) => {
    if(question.questionType === 'single') {
        if(question.question.type !== 'heading') {  // headings should never happen in this case
            const mainProperty = question.question.properties[0];            
            const selectedValue = values[mainProperty];
            const implicitValue = implicitValues[mainProperty];
            return <ConfiguratorSingleQuestion question={question} step={step} questionData={questionData} 
                                               selectedValue={selectedValue} implicitValue={implicitValue} 
                                               values={values} maxShowablePerCounter={maxShowablePerCounter}
                                               onValuesChange={onValuesChange} onQuestionDataChange={onQuestionDataChange}/>    
        }
    } else {
        return <ConfiguratorQuestionGroup group={question.group} questions={question.questions} 
                                          step={step} questionData={questionData} values={values}  
                                          implicitValues={implicitValues}
                                          maxShowablePerCounter={maxShowablePerCounter}
                                          onValuesChange={onValuesChange} onQuestionDataChange={onQuestionDataChange}/>
    }
}

const ConfiguratorSingleQuestion = ({ question, step, questionData, selectedValue, 
                                      implicitValue, values, maxShowablePerCounter,
                                      onValuesChange, onQuestionDataChange,  }: {
    question: CfgViewQuestionSingle
    step: number
    questionData?: any
    selectedValue: string | null
    implicitValue: string | null
    values: OptionValues
    maxShowablePerCounter: Record<string,number>
    onValuesChange: (values: OptionValues, forceApplytoAll: boolean) => void
    onQuestionDataChange: (data: any, interaction: MaterialQuestionInteraction) => void
}) => {

    const onValuesChangeWithApplyAll = (values: OptionValues) => {
        onValuesChange(values, question.question.applyToAll ?? false);
    }

    if(question.question.type === 'enum') {

        const enumQuestion = question.question;

        const _onValuesChange = (values: string[]) => {
            const payload: Record<string,string> = {};
            let i = 0;
            for(const property of enumQuestion.properties) {
                payload[property] = values[i];
                i += 1;
            }
            onValuesChangeWithApplyAll(payload);
        }
        return <ConfiguratorEnumQuestion question={enumQuestion} selectedValue={selectedValue} values={question.values} onValuesChange={_onValuesChange}/>

    } else if(question.question.type === 'material') {

        const matQuestion = question.question;

        const onValueChange = (value: string|null) => {
            const payload: OptionValues = {};
            for(const property of matQuestion.properties) {
                payload[property] = value;
            }
            onValuesChangeWithApplyAll(payload);
        }

        return <ConfiguratorMaterialQuestion 
                    question={question.question} step={step} 
                    selectedValue={selectedValue} implicitValue={implicitValue} onValueChange={onValueChange} 
                    onQuestionDataChange={onQuestionDataChange}/>

    } else if(question.question.type === 'quantity') {

        const enumQuestion = question.question;

        const _onValuesChange = (values: string[], quantity: number) => {
            const payload: Record<string,string> = {};
            let i = 0;
            for(const property of enumQuestion.properties) {
                payload[property] = values[i];
                i += 1;
            }
            payload[enumQuestion.quantityProperty] = quantity.toString();
            onValuesChangeWithApplyAll(payload);
        }

        const quantity = Number(values[question.question.quantityProperty] ?? 1);
        const maxShownQuantity = maxShowablePerCounter[question.question.quantityProperty];
        
        return( 
            <ConfiguratorQuantityQuestion 
                question={enumQuestion} 
                selectedValue={selectedValue ?? null} 
                values={question.values}
                maxShowableQuantity={maxShownQuantity}
                quantity={quantity} 
                onValuesChange={_onValuesChange}/>
        )

    } else {
        return null;    // headings should never happen in this case
    }
}

const ConfiguratorQuestionGroup = ({ group, questions, step, questionData, values, maxShowablePerCounter,
                                     onValuesChange, onQuestionDataChange } : {
    group: CfgQuestionGroup,
    questions: CfgViewQuestionSingle[]
    step: number
    questionData?: any
    values: OptionValues
    implicitValues: OptionValues
    maxShowablePerCounter: Record<string,number>
    onValuesChange: (values: OptionValues, forceApplytoAll: boolean) => void
    onQuestionDataChange: (data: any, interaction: MaterialQuestionInteraction) => void
}) => {
    return <>
    {
        questions.map((question) => {
            if(question.question.type === 'heading') {
                return <ConfiguratorHeading key={question.question.id} heading={question.question}/>
            } else {
                const mainProperty = question.question.properties[0];
                const selectedValue = values[mainProperty];
                const implicitValue = values[mainProperty];
                return <ConfiguratorSingleQuestion key={question.question.id} question={question} 
                                                   step={step} questionData={questionData}
                                                   selectedValue={selectedValue} implicitValue={implicitValue} values={values}
                                                   maxShowablePerCounter={maxShowablePerCounter}
                                                   onValuesChange={onValuesChange} onQuestionDataChange={onQuestionDataChange}/>    
            }
        })
    }
    </>
}